import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component, OnInit, ViewEncapsulation, TemplateRef, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';
import { catchError, of } from 'rxjs';
import { JobDetail, JobViewService } from './job-view.service';
import { Meta, Title } from '@angular/platform-browser';
import Swal from 'sweetalert2';

import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Allows spaces ONLY after minChars characters
 * Example: "John Doe" ✅
 * "Jo n" ❌
 * "   John" ❌
 */
export function minCharsThenSpace(minChars: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    const trimmed = value.trimStart();

    const regex = new RegExp(`^[A-Za-z]{${minChars},}([ A-Za-z]*)$`);
    return regex.test(trimmed) ? null : { minCharsThenSpace: true };
  };
}

/**
 * No spaces allowed at all
 */
export function noSpacesValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) return null;
  return /\s/.test(control.value) ? { noSpaces: true } : null;
}

/**
 * Numbers only (no spaces, no text)
 */
export function numbersOnlyValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) return null;
  return /^[0-9]+$/.test(control.value)
    ? null
    : { numbersOnly: true };
}


@Component({
  selector: 'app-job-view',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, HttpClientModule,RouterLink],
  templateUrl: './job-view.component.html',
  styleUrls: ['./job-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class JobViewComponent implements OnInit {
  @ViewChild('errorTpl', { static: true }) errorTpl!: TemplateRef<any>;

  applyForm: FormGroup;
  resumeFile: File | null = null;
  fileError: boolean = false;

  jobId: number = 0;
  // allow additional props (like image) that may not exist on JobDetail interface
  jobDetail: JobDetail | any | null = null;

  loading = true;
  errorMessage = '';
  submitting = false;
  successMessage = '';
  errorMessageForm = '';

  // ensure currentStep is a number (comparison with 2 becomes valid)
  currentStep: number = 1; // STEP 1 → description, STEP 2 → form

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private jobService: JobViewService,
    private applyService: JobViewService,
    private titleService: Title,
    private metaService: Meta
  ) {
    this.applyForm = this.fb.group({
      fullName: ['',  [Validators.required, minCharsThenSpace(3)],],
      email: ['',  [Validators.required, Validators.email, noSpacesValidator],],
      phone: ['', [
      Validators.required,
      Validators.pattern('^[0-9]{10}$'),
      noSpacesValidator,
    ],],
      location: ['', [Validators.required, minCharsThenSpace(3)],],
      portfolio: ['', Validators.pattern('https?://.+')],
      experience: ['',   [
      Validators.required,
      numbersOnlyValidator,
      noSpacesValidator,
    ],],
      agreement: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.jobId = +params['jobId'];
      if (this.jobId) {
        this.fetchJobDetail(this.jobId);
      } else {
        this.errorMessage = 'Invalid Job ID';
        this.loading = false;
      }
    });

    this.titleService.setTitle('MALtech | Job Details');

    // Set meta description
    this.metaService.updateTag({
      name: 'description',
      content:
        'Sign up for MALtech to access our services. Fill out the form with your details to create an account.',
    });

    this.metaService.updateTag({
      name: 'keywords',
      content: 'signup, MALtech, IT services, create account',
    });
    this.metaService.updateTag({ name: 'author', content: 'Maltech' });
  }

  get formattedDescription(): string {
  if (!this.jobDetail?.description) return '';

  return this.jobDetail.description
    .replace(/<ol>/g, '<ul>')
    .replace(/<\/ol>/g, '</ul>');
}


fetchJobDetail(jobId: number) {
  this.loading = true;

  this.jobService.getJobDetail(jobId).subscribe({
    next: (res) => {
      this.loading = false;

      if (res) {
        this.jobDetail = {
          ...res,
          image: (res as any).image ?? undefined, // API has no image field yet; keep for future
        };
      } else {
        this.errorMessage = 'No details found for this job.';
        this.jobDetail = null;
      }
    },
    error: (err) => {
      this.loading = false;
      this.errorMessage = 'Failed to load job details.';
      this.jobDetail = null;
      console.error('fetchJobDetail error', err);
    },
  });
}

        
  goToApply() {
    this.currentStep = 2;
  }

  onFileChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.resumeFile = file;
      this.fileError = false;
    }
  }

  onSubmit() {
    if (!this.resumeFile) {
      this.fileError = true;
    }

    if (this.applyForm.valid && this.resumeFile) {
      this.submitting = true;
      const formValue = this.applyForm.value;

      const submissionData = {
        jobId: this.jobId,
        fullName: formValue.fullName,
        email: formValue.email,
        phone: formValue.phone,
        location: formValue.location,
        portfolio: formValue.portfolio,
        experience: formValue.experience,
        resume: this.resumeFile,
      };

      this.applyService.applyJob(submissionData).subscribe({
        next: (res) => {
          this.submitting = false;
          if (res.success) {
            Swal.fire({
              title: 'Success',
              icon: 'success',
              showCloseButton: true,
              confirmButtonText: 'OK',
              confirmButtonColor: '#009fe4',
              timer: 5000,
              timerProgressBar: true,
            });
            this.successMessage = res.message || 'Application submitted successfully!';
            this.applyForm.reset();
            this.resumeFile = null;
            this.fileError = false;
            this.errorMessageForm = '';
          } else {
            Swal.fire({
              title: 'Error',
              text: res.message || 'Failed to submit application.',
              icon: 'error',
              confirmButtonColor: '#ff4d4f',
            });
            this.errorMessageForm = res.message || 'Failed to submit application.';
            this.submitting = false;
          }
        },
        error: (err) => {
          console.error(err);
          this.submitting = false;
          Swal.fire({
            title: 'Error',
            text: 'Failed to submit application.',
            icon: 'error',
            confirmButtonColor: '#ff4d4f',
          });
        },
      });
    } else {
      this.applyForm.markAllAsTouched();
      if (!this.resumeFile) this.fileError = true;
    }
  }
}
